import React, { useState } from 'react';
import {
  AreaChart, Area, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AssessmentIcon from '@mui/icons-material/Assessment';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

// ─── Mock Data ────────────────────────────────────────────────────────────────

const plEvolution = [
  { mes: 'Abr', pl: 980 }, { mes: 'Mai', pl: 1020 }, { mes: 'Jun', pl: 1055 },
  { mes: 'Jul', pl: 1030 }, { mes: 'Ago', pl: 1090 }, { mes: 'Set', pl: 1130 },
  { mes: 'Out', pl: 1100 }, { mes: 'Nov', pl: 1160 }, { mes: 'Dez', pl: 1190 },
  { mes: 'Jan', pl: 1145 }, { mes: 'Fev', pl: 1210 }, { mes: 'Mar', pl: 1248 },
];

const alocacaoClasses = [
  { name: 'Renda Fixa Corporativo', value: 38, color: '#2C91AD' },
  { name: 'Renda Fixa Bancário',    value: 22, color: '#206A7F' },
  { name: 'Títulos Públicos',       value: 18, color: '#54B8D3' },
  { name: 'Renda Variável',         value: 12, color: '#29A152' },
  { name: 'Outros',                 value: 10, color: '#CFD8E5' },
];

const fundos = [
  { nome: 'AGRELO STOCK FC FIA',     pl: 287.4, var: +1.8,  cotas: 1204, adimplencia: 98.2 },
  { nome: 'TRIUMPH HY FC FIM CP',    pl: 214.6, var: +0.6,  cotas: 983,  adimplencia: 97.1 },
  { nome: 'FORTIS FIM',              pl: 189.3, var: -0.4,  cotas: 876,  adimplencia: 99.0 },
  { nome: 'GUALTALLARY FC FIM',      pl: 162.8, var: +2.1,  cotas: 741,  adimplencia: 96.8 },
  { nome: 'HORIZON SELECT FIM',      pl: 148.2, var: +0.9,  cotas: 634,  adimplencia: 98.5 },
  { nome: 'BRUGES MID FC FIM',       pl: 121.7, var: -1.2,  cotas: 502,  adimplencia: 95.3 },
  { nome: 'EDDA INFLA PRO FIM',      pl: 98.4,  var: +0.3,  cotas: 445,  adimplencia: 97.6 },
  { nome: 'KALYTERA PREV FC FIM',    pl: 76.1,  var: +1.4,  cotas: 338,  adimplencia: 99.1 },
];

const plMaxFundo = Math.max(...fundos.map(f => f.pl));

const alertas = [
  { tipo: 'warning', titulo: 'Concentração elevada',   descricao: 'AGRELO STOCK: 23% acima do limite de classe', tempo: '10 min' },
  { tipo: 'error',   titulo: 'Divergência de PL',      descricao: 'BRUGES MID: PL calculado vs. BTG — Δ R$ 12.400', tempo: '1h' },
  { tipo: 'info',    titulo: 'Carteira processada',     descricao: 'KALYTERA PREV importada com sucesso (16/03)', tempo: '2h' },
  { tipo: 'info',    titulo: 'Carteira processada',     descricao: 'EDDA INFLA PRO importada com sucesso (16/03)', tempo: '2h' },
  { tipo: 'warning', titulo: 'Taxa de inadimplência',  descricao: 'BRUGES MID: 4,7% — acima do benchmark de 3%', tempo: '3h' },
  { tipo: 'success', titulo: 'Conformidade OK',        descricao: 'HORIZON SELECT: enquadramento validado', tempo: '5h' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmtBRL = (v: number) =>
  v.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

const alertIcon = (tipo: string) => {
  if (tipo === 'error')   return <ErrorOutlineIcon   style={{ fontSize: 16, color: '#E51739' }} />;
  if (tipo === 'warning') return <WarningAmberIcon   style={{ fontSize: 16, color: '#F27A00' }} />;
  if (tipo === 'success') return <CheckCircleOutlineIcon style={{ fontSize: 16, color: '#29A152' }} />;
  return                         <InfoOutlinedIcon   style={{ fontSize: 16, color: '#2983CC' }} />;
};

const alertDot = (tipo: string) => {
  const colors: Record<string, string> = {
    error: '#E51739', warning: '#F27A00', success: '#29A152', info: '#2983CC',
  };
  return colors[tipo] || '#2983CC';
};

// ─── Sub-components ───────────────────────────────────────────────────────────

interface KPICardProps {
  label: string;
  value: string;
  sub?: string;
  delta?: number;
  icon: React.ReactNode;
  accent?: string;
}

const KPICard: React.FC<KPICardProps> = ({ label, value, sub, delta, icon, accent = '#2C91AD' }) => {
  const up = delta !== undefined && delta >= 0;
  return (
    <div style={{
      background: '#fff',
      border: '1px solid #CFD8E5',
      borderRadius: 12,
      padding: '20px 22px',
      display: 'flex',
      flexDirection: 'column',
      gap: 10,
      flex: 1,
      minWidth: 0,
      boxShadow: '0 1px 3px rgba(29,29,38,0.05)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 12, fontWeight: 500, color: '#4D5766', letterSpacing: '0.02em', textTransform: 'uppercase' }}>
          {label}
        </span>
        <div style={{
          width: 32, height: 32, borderRadius: 8,
          background: accent + '18',
          display: 'flex', alignItems: 'center', justifyContent: 'center', color: accent,
        }}>
          {icon}
        </div>
      </div>
      <div>
        <div style={{ fontSize: 26, fontWeight: 700, color: '#1D1D26', lineHeight: 1.1, fontFamily: 'Montserrat, sans-serif' }}>
          {value}
        </div>
        {sub && <div style={{ fontSize: 12, color: '#8A98A8', marginTop: 3 }}>{sub}</div>}
      </div>
      {delta !== undefined && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {up
            ? <TrendingUpIcon style={{ fontSize: 14, color: '#29A152' }} />
            : <TrendingDownIcon style={{ fontSize: 14, color: '#E51739' }} />}
          <span style={{ fontSize: 12, fontWeight: 600, color: up ? '#29A152' : '#E51739' }}>
            {up ? '+' : ''}{delta.toFixed(1)}% vs mês anterior
          </span>
        </div>
      )}
    </div>
  );
};

const CustomTooltipPL = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: '#1D1D26', color: '#fff', borderRadius: 8,
      padding: '8px 14px', fontSize: 12, lineHeight: 1.6,
    }}>
      <div style={{ fontWeight: 600 }}>{label}</div>
      <div>R$ {fmtBRL(payload[0].value)} M</div>
    </div>
  );
};

const CustomTooltipPie = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: '#1D1D26', color: '#fff', borderRadius: 8,
      padding: '8px 14px', fontSize: 12,
    }}>
      <div style={{ fontWeight: 600 }}>{payload[0].name}</div>
      <div>{payload[0].value}%</div>
    </div>
  );
};

// ─── Dashboard ────────────────────────────────────────────────────────────────

const hoje = new Date().toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
const totalPL = fundos.reduce((s, f) => s + f.pl, 0);
const inadMedia = fundos.reduce((s, f) => s + (100 - f.adimplencia), 0) / fundos.length;

const DashboardPage: React.FC = () => {
  const [fundoHover, setFundoHover] = useState<number | null>(null);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

      {/* ── Cabeçalho ── */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.45rem' }}>Visão Geral</h2>
          <p style={{ margin: 0, color: '#8A98A8', fontSize: 13 }}>{hoje.charAt(0).toUpperCase() + hoje.slice(1)}</p>
        </div>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6,
          background: '#DDF5E4', borderRadius: 20, padding: '5px 12px',
        }}>
          <FiberManualRecordIcon style={{ fontSize: 8, color: '#29A152' }} />
          <span style={{ fontSize: 12, fontWeight: 600, color: '#1D7246' }}>
            {fundos.length} FIDCs processados hoje
          </span>
        </div>
      </div>

      {/* ── KPI Cards ── */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <KPICard
          label="Patrimônio Líquido Total"
          value={`R$ ${fmtBRL(totalPL)} M`}
          sub="Consolidado em 16/03/2026"
          delta={+2.3}
          icon={<AccountBalanceIcon style={{ fontSize: 16 }} />}
          accent="#2C91AD"
        />
        <KPICard
          label="FIDCs Ativos"
          value={`${fundos.length}`}
          sub="Carteiras sob gestão"
          icon={<AssessmentIcon style={{ fontSize: 16 }} />}
          accent="#206A7F"
        />
        <KPICard
          label="Carteiras Processadas"
          value={`${fundos.length}/${fundos.length}`}
          sub="Importações do dia"
          delta={0}
          icon={<CheckCircleOutlineIcon style={{ fontSize: 16 }} />}
          accent="#29A152"
        />
        <KPICard
          label="Alertas Operacionais"
          value="3"
          sub="2 avisos · 1 erro"
          icon={<WarningAmberIcon style={{ fontSize: 16 }} />}
          accent="#F27A00"
        />
        <KPICard
          label="Inadimplência Média"
          value={`${inadMedia.toFixed(1)}%`}
          sub="Média ponderada da carteira"
          delta={-0.2}
          icon={<TrendingDownIcon style={{ fontSize: 16 }} />}
          accent="#E51739"
        />
      </div>

      {/* ── Row 2: Gráficos ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16 }}>

        {/* Evolução do PL */}
        <div style={{
          background: '#fff', border: '1px solid #CFD8E5', borderRadius: 12,
          padding: '22px 24px', boxShadow: '0 1px 3px rgba(29,29,38,0.05)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14, color: '#1D1D26', fontFamily: 'Montserrat, sans-serif' }}>
                Evolução do Patrimônio Líquido
              </div>
              <div style={{ fontSize: 12, color: '#8A98A8', marginTop: 2 }}>Últimos 12 meses · em R$ Milhões</div>
            </div>
            <div style={{
              background: '#EEF7FA', borderRadius: 20, padding: '4px 12px',
              fontSize: 12, fontWeight: 600, color: '#2C91AD',
            }}>
              +27,3% a.a.
            </div>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={plEvolution} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="gradPL" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#2C91AD" stopOpacity={0.18} />
                  <stop offset="95%" stopColor="#2C91AD" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#F0F4F8" vertical={false} />
              <XAxis dataKey="mes" tick={{ fontSize: 11, fill: '#8A98A8' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: '#8A98A8' }} axisLine={false} tickLine={false} domain={['auto', 'auto']} />
              <Tooltip content={<CustomTooltipPL />} cursor={{ stroke: '#CFD8E5', strokeWidth: 1 }} />
              <Area
                type="monotone" dataKey="pl"
                stroke="#2C91AD" strokeWidth={2.5}
                fill="url(#gradPL)"
                dot={false} activeDot={{ r: 4, fill: '#2C91AD', strokeWidth: 2, stroke: '#fff' }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Alocação por Classe */}
        <div style={{
          background: '#fff', border: '1px solid #CFD8E5', borderRadius: 12,
          padding: '22px 24px', boxShadow: '0 1px 3px rgba(29,29,38,0.05)',
          display: 'flex', flexDirection: 'column',
        }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: '#1D1D26', fontFamily: 'Montserrat, sans-serif', marginBottom: 4 }}>
            Alocação por Classe
          </div>
          <div style={{ fontSize: 12, color: '#8A98A8', marginBottom: 16 }}>Distribuição consolidada</div>

          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <ResponsiveContainer width={160} height={160}>
              <PieChart>
                <Pie data={alocacaoClasses} cx="50%" cy="50%"
                  innerRadius={48} outerRadius={72}
                  paddingAngle={3} dataKey="value"
                >
                  {alocacaoClasses.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltipPie />} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
            {alocacaoClasses.map((c, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 8, height: 8, borderRadius: 2, background: c.color, flexShrink: 0 }} />
                  <span style={{ fontSize: 12, color: '#4D5766' }}>{c.name}</span>
                </div>
                <span style={{ fontSize: 12, fontWeight: 600, color: '#1D1D26' }}>{c.value}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Row 3: Resumo fundos + Alertas ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '3fr 2fr', gap: 16 }}>

        {/* Resumo por Fundo */}
        <div style={{
          background: '#fff', border: '1px solid #CFD8E5', borderRadius: 12,
          padding: '22px 24px', boxShadow: '0 1px 3px rgba(29,29,38,0.05)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14, color: '#1D1D26', fontFamily: 'Montserrat, sans-serif' }}>
                Resumo por Fundo
              </div>
              <div style={{ fontSize: 12, color: '#8A98A8', marginTop: 2 }}>PL · Variação · Adimplência</div>
            </div>
          </div>

          {/* Cabeçalho */}
          <div style={{
            display: 'grid', gridTemplateColumns: '1fr 90px 80px 70px',
            fontSize: 11, fontWeight: 600, color: '#8A98A8',
            textTransform: 'uppercase', letterSpacing: '0.04em',
            paddingBottom: 10, borderBottom: '1px solid #F0F4F8', marginBottom: 4,
          }}>
            <span>Fundo</span>
            <span style={{ textAlign: 'right' }}>PL (R$ M)</span>
            <span style={{ textAlign: 'right' }}>Var. dia</span>
            <span style={{ textAlign: 'right' }}>Adimpl.</span>
          </div>

          {fundos.map((f, i) => (
            <div
              key={i}
              onMouseEnter={() => setFundoHover(i)}
              onMouseLeave={() => setFundoHover(null)}
              style={{
                display: 'grid', gridTemplateColumns: '1fr 90px 80px 70px',
                alignItems: 'center',
                padding: '9px 8px',
                borderRadius: 8,
                background: fundoHover === i ? '#F8FAFC' : 'transparent',
                transition: 'background 0.12s',
                cursor: 'default',
              }}
            >
              {/* Nome + barra de PL */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, paddingRight: 12 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: '#1D1D26', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {f.nome}
                </span>
                <div style={{ height: 3, borderRadius: 2, background: '#F0F4F8', overflow: 'hidden' }}>
                  <div style={{
                    height: '100%', borderRadius: 2,
                    background: '#2C91AD',
                    width: `${(f.pl / plMaxFundo) * 100}%`,
                    transition: 'width 0.3s',
                  }} />
                </div>
              </div>

              <span style={{ fontSize: 13, fontWeight: 600, color: '#1D1D26', textAlign: 'right' }}>
                {fmtBRL(f.pl)}
              </span>

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 3 }}>
                {f.var >= 0
                  ? <TrendingUpIcon style={{ fontSize: 13, color: '#29A152' }} />
                  : <TrendingDownIcon style={{ fontSize: 13, color: '#E51739' }} />}
                <span style={{ fontSize: 12, fontWeight: 600, color: f.var >= 0 ? '#29A152' : '#E51739' }}>
                  {f.var >= 0 ? '+' : ''}{f.var.toFixed(1)}%
                </span>
              </div>

              <div style={{ textAlign: 'right' }}>
                <span style={{
                  fontSize: 12, fontWeight: 600,
                  color: f.adimplencia >= 98 ? '#29A152' : f.adimplencia >= 96 ? '#F27A00' : '#E51739',
                }}>
                  {f.adimplencia.toFixed(1)}%
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Alertas Operacionais */}
        <div style={{
          background: '#fff', border: '1px solid #CFD8E5', borderRadius: 12,
          padding: '22px 24px', boxShadow: '0 1px 3px rgba(29,29,38,0.05)',
          display: 'flex', flexDirection: 'column',
        }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: '#1D1D26', fontFamily: 'Montserrat, sans-serif', marginBottom: 4 }}>
            Alertas Operacionais
          </div>
          <div style={{ fontSize: 12, color: '#8A98A8', marginBottom: 18 }}>Atividade recente</div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 0, flex: 1 }}>
            {alertas.map((a, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'flex-start', gap: 12,
                padding: '11px 0',
                borderBottom: i < alertas.length - 1 ? '1px solid #F0F4F8' : 'none',
              }}>
                {/* Ícone */}
                <div style={{
                  width: 28, height: 28, borderRadius: 8, flexShrink: 0,
                  background: alertDot(a.tipo) + '14',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {alertIcon(a.tipo)}
                </div>

                {/* Conteúdo */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#1D1D26', marginBottom: 1 }}>
                    {a.titulo}
                  </div>
                  <div style={{
                    fontSize: 11, color: '#4D5766', lineHeight: 1.4,
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>
                    {a.descricao}
                  </div>
                </div>

                {/* Tempo */}
                <span style={{ fontSize: 11, color: '#8A98A8', flexShrink: 0, paddingTop: 1 }}>
                  {a.tempo}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

    </div>
  );
};

export default DashboardPage;
